import * as React from "react";
import SideNavigation from "@amzn/awsui-components-react/polaris/side-navigation";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import Container from "@amzn/awsui-components-react/polaris/container";
import Select from "@amzn/awsui-components-react/polaris/select";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Link from "@amzn/awsui-components-react/polaris/link";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import PageShell from "../../../../global/components/PageShell";
import { readHash, setHash, syncHash, initHashNav } from "../../../utils/hashNav";
import { ALL_CUSTOMER_TYPE_SECTIONS, CustomerTypeSection } from "../constants/all-sections-config";
import { API } from "aws-amplify";
import CreateSubscriptionResult from "../../update_pharmacy_customer/components/subscription/components/create_subscription/CreateSubscriptionResult";

interface CompleteFillResult {
    fillId: string;
    prescriptionId: string;
    success: boolean;
    message?: string;
}

interface State {
    activeSection: string;
    selectedOption: { value: string; label: string; description?: string } | null;
    navOpen: boolean;
    isLoading: boolean;
    errorMessage: string;
    response: any;
    resultSuccessful: boolean;
    availableCount: number | null;
    countLoading: boolean;
    sortOrder: string;
    completingFills: boolean;
    completeFillResults: CompleteFillResult[];
}

class CachedPharmacyCustomerProviderPage extends React.Component<any, State> {
    private cleanupHashNav: (() => void) | null = null;

    constructor(props: any) {
        super(props);

        const hash = readHash();
        const { section, entryKey } = this.parseHash(hash);
        const activeSection = section || ALL_CUSTOMER_TYPE_SECTIONS[0].title;
        const selectedOption = entryKey ? this.buildOptionFromKey(activeSection, entryKey) : null;

        this.state = {
            activeSection,
            selectedOption,
            navOpen: true,
            isLoading: false,
            errorMessage: "",
            response: null,
            resultSuccessful: false,
            availableCount: null,
            countLoading: false,
            sortOrder: "oldest",
            completingFills: false,
            completeFillResults: [],
        };
    }

    componentDidMount() {
        syncHash(this.buildHash());
        this.cleanupHashNav = initHashNav((hash) => {
            // Guard: skip if hash matches current state (avoids duplicate setState/fetchCount from our own setHash calls)
            if (hash === this.buildHash()) return;
            const { section, entryKey } = this.parseHash(hash);
            if (!section) return;
            const selectedOption = entryKey ? this.buildOptionFromKey(section, entryKey) : null;
            this.setState({
                activeSection: section,
                selectedOption,
                resultSuccessful: false,
                response: null,
                errorMessage: "",
                availableCount: null,
                countLoading: false,
                completeFillResults: [],
            }, () => {
                if (selectedOption) this.fetchCount(selectedOption.value);
            });
        });
        // Fetch count if a dropdown item was restored from hash
        if (this.state.selectedOption) {
            this.fetchCount(this.state.selectedOption.value);
        }
    }

    componentWillUnmount() {
        this.cleanupHashNav?.();
    }

    private parseHash(hash: string): { section: string | null; entryKey: string | null } {
        if (!hash) return { section: null, entryKey: null };
        const slashIdx = hash.indexOf("/");
        if (slashIdx === -1) {
            const section = ALL_CUSTOMER_TYPE_SECTIONS.find(s => s.title === hash) ? hash : null;
            return { section, entryKey: null };
        }
        const sectionPart = hash.substring(0, slashIdx);
        const entryPart = hash.substring(slashIdx + 1);
        const section = ALL_CUSTOMER_TYPE_SECTIONS.find(s => s.title === sectionPart) ? sectionPart : null;
        return { section, entryKey: section ? entryPart : null };
    }

    private buildHash(): string {
        const { activeSection, selectedOption } = this.state;
        return selectedOption ? `${activeSection}/${selectedOption.value}` : activeSection;
    }

    private buildOptionFromKey(sectionTitle: string, key: string): { value: string; label: string; description?: string } | null {
        const section = ALL_CUSTOMER_TYPE_SECTIONS.find(s => s.title === sectionTitle);
        if (!section) return null;
        const idx = section.entries.findIndex(e => e.key === key);
        if (idx === -1) return null;
        const entry = section.entries[idx];
        const { customerType, configType } = this.parsePathParams(entry.path);
        return {
            value: entry.key,
            label: `${idx + 1}. ${entry.description}`,
            description: `Type: ${customerType} | Config: ${configType}`,
        };
    }

    private get currentSection(): CustomerTypeSection | undefined {
        return ALL_CUSTOMER_TYPE_SECTIONS.find(s => s.title === this.state.activeSection);
    }

    private parsePathParams(path: string): { customerType: string; configType: string } {
        const params = new URLSearchParams(path);
        return {
            customerType: params.get("defaultPharmacyCustomerTypes") || "Unknown",
            configType: params.get("pharmacyCustomerConfigTypes") || "Unknown",
        };
    }

    private get selectOptions() {
        const section = this.currentSection;
        if (!section) return [];
        return section.entries.map((entry, idx) => {
            const { customerType, configType } = this.parsePathParams(entry.path);
            return {
                value: entry.key,
                label: `${idx + 1}. ${entry.description}`,
                description: `Type: ${customerType} | Config: ${configType}`,
            };
        });
    }

    private handleSelectionChange = (selectedOption: any) => {
        this.setState({ selectedOption, availableCount: null, countLoading: false }, () => {
            setHash(this.buildHash());
        });
        if (selectedOption) {
            this.fetchCount(selectedOption.value);
        }
    };

    private fetchCount = (key: string) => {
        const section = this.currentSection;
        if (!section) return;

        const entry = section.entries.find(e => e.key === key);
        if (!entry) return;

        this.setState({ countLoading: true });

        const apiName = "ApaqPortalAPI";
        const path = `/cached-pharmacy-customer/get-cached-pharmacy-customer-count?${entry.path}`;

        API.get(apiName, path, {})
            .then((response: any) => {
                const count = typeof response === "number"
                    ? response
                    : response?.cpcpCustomerCount ?? response?.count ?? response?.totalCount ?? response?.readyCount ?? null;
                this.setState({ availableCount: count, countLoading: false });
            })
            .catch(() => {
                this.setState({ availableCount: null, countLoading: false });
            });
    };

    private renderCountBadge() {
        const { countLoading, availableCount } = this.state;

        if (countLoading) {
            return <Spinner size="normal" />;
        }

        if (availableCount === null) {
            return null;
        }

        const color = availableCount === 0 ? "#d13212" : availableCount <= 3 ? "#ff9900" : "#1d8102";
        const bgColor = availableCount === 0 ? "#fdf3f1" : availableCount <= 3 ? "#fef8f0" : "#f2f8f0";
        const label = availableCount === 0 ? "Out of stock" : "In stock";

        return (
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 14px",
                borderRadius: "8px",
                backgroundColor: bgColor,
                border: `1px solid ${color}20`,
            }}>
                <div style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: color,
                    boxShadow: `0 0 6px ${color}80`,
                }} />
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <span style={{ fontSize: "18px", fontWeight: 700, color }}>{availableCount.toLocaleString()}</span>
                    <span style={{ fontSize: "11px", color: "#5f6b7a" }}>{label}</span>
                </div>
            </div>
        );
    }

    private handleSubmit = () => {
        const section = this.currentSection;
        const selected = this.state.selectedOption;
        if (!section || !selected) return;

        const entry = section.entries.find(e => e.key === selected.value);
        if (!entry) {
            this.setState({ errorMessage: "No such customer type" });
            return;
        }

        this.setState({ isLoading: true, errorMessage: "", resultSuccessful: false, response: null, completeFillResults: [] });

        const apiName = "ApaqPortalAPI";
        let path = `/cached-pharmacy-customer/get?${entry.path}`;

        // Add sort order for FFR Concessions section
        if (this.state.activeSection === "FFR Concessions") {
            path += `&sortOrder=${this.state.sortOrder}`;
        }

        API.get(apiName, path, {})
            .then(async (response: any) => {
                // Auto-complete fills for Consumption Guard customers before showing results
                if (this.state.activeSection === "Consumption Guard") {
                    await this.completeFillsForConsumptionGuard(response);
                }
                this.setState({ response, isLoading: false, resultSuccessful: true });
                // Refresh count after claiming a customer
                if (this.state.selectedOption) {
                    this.fetchCount(this.state.selectedOption.value);
                }
            })
            .catch((error: any) => {
                this.setState({
                    errorMessage: error.response?.data?.message || error.message || "An error occurred",
                    isLoading: false,
                });
            });
    };

    // TODO: This is a temporary measure to accommodate tight timelines. We should consider the proper strategy to
    //  COMPLETE fills to avoid order cancellation when shipment is not generated
    private completeFillsForConsumptionGuard = async (response: any) => {
        const { personId, profileId, orderMetadata } = response;
        if (!personId || !profileId || !orderMetadata || orderMetadata.length === 0) return;

        this.setState({ completingFills: true, completeFillResults: [] });

        const results: CompleteFillResult[] = [];
        const prescriptionIds = [...new Set(
            orderMetadata.map((entry: any) => entry.prescriptionId).filter(Boolean)
        )] as string[];

        for (const prescriptionId of prescriptionIds) {
            try {
                const rxResponse = await API.get("ApaqPortalAPI",
                    `/prescription/search?prescriptionId=${prescriptionId}`, {});
                const fills = rxResponse?.fills || [];
                const purchasedFills = fills.filter((f: any) => f.status === "PURCHASED");

                for (const fill of purchasedFills) {
                    try {
                        await API.post("ApaqPortalAPI", "/prescription/fills/complete", {
                            body: { fillId: fill.fillId, personId, prescriptionId, profileId },
                            headers: {},
                        });
                        results.push({ fillId: fill.fillId, prescriptionId, success: true });
                    } catch (error) {
                        const err = error as any;
                        results.push({
                            fillId: fill.fillId,
                            prescriptionId,
                            success: false,
                            message: err.response?.data?.message || err.message || "Unknown error",
                        });
                    }
                }
            } catch (error) {
                const err = error as any;
                results.push({
                    fillId: "unknown",
                    prescriptionId,
                    success: false,
                    message: `Failed to lookup prescription: ${err.response?.data?.message || err.message || "Unknown error"}`,
                });
            }
        }

        this.setState({ completingFills: false, completeFillResults: results });
    };

    render() {
        return (
            <PageShell
                navigationOpen={this.state.navOpen}
                onNavigationChange={(open) => this.setState({ navOpen: open })}
                navigation={
                    <SideNavigation
                        header={{ text: "Cached Customers", href: `#${encodeURIComponent(ALL_CUSTOMER_TYPE_SECTIONS[0].title)}` }}
                        activeHref={`#${encodeURIComponent(this.state.activeSection)}`}
                        onFollow={(e) => {
                            e.preventDefault();
                            const sectionTitle = decodeURIComponent(e.detail.href.replace(/^#/, ""));
                            this.setState({
                                activeSection: sectionTitle,
                                selectedOption: null,
                                resultSuccessful: false,
                                response: null,
                                errorMessage: "",
                                availableCount: null,
                                countLoading: false,
                                sortOrder: "oldest",
                                completeFillResults: [],
                            }, () => {
                                setHash(this.buildHash());
                            });
                        }}
                        items={ALL_CUSTOMER_TYPE_SECTIONS.map(section => ({
                            type: "link" as const,
                            text: `${section.title} (${section.entries.length})`,
                            href: `#${encodeURIComponent(section.title)}`,
                        }))}
                    />
                }
                content={
                    <div>
                        <Box padding={{ top: "l", bottom: "l" }}>
                            <Header
                                variant="h1"
                                description={
                                    <Link
                                        href="https://w.amazon.com/bin/view/Pharmacy/Public/Teams/PharmacySDET/CPCPCustomerDashboard-ApaqPortalUIBeta/"
                                        external
                                    >
                                        CPCP Customer Inventory Dashboard
                                    </Link>
                                }
                            >
                                Cached Pharmacy Customers
                            </Header>
                        </Box>

                        <SpaceBetween size="l">
                            <Container
                                header={
                                    <Header variant="h2" counter={`(${this.currentSection?.entries.length || 0})`}>
                                        {this.state.activeSection}
                                    </Header>
                                }
                            >
                                <SpaceBetween size="m">
                                    {this.state.activeSection === "FFR Concessions" && (
                                        <Alert type="info">
                                            These customers are only ship-completed. You must run the FFR concession process separately after retrieving the customer.
                                        </Alert>
                                    )}
                                    <SpaceBetween size="s" direction="horizontal">
                                        <div style={{ width: "800px" }}>
                                            <Select
                                                selectedOption={this.state.selectedOption}
                                                onChange={({ detail }) =>
                                                    this.handleSelectionChange(detail.selectedOption)
                                                }
                                                options={this.selectOptions}
                                                placeholder="Select a customer type"
                                                filteringType="auto"
                                            />
                                        </div>
                                        <Button
                                            variant="primary"
                                            onClick={this.handleSubmit}
                                            disabled={!this.state.selectedOption}
                                            loading={this.state.isLoading}
                                        >
                                            Get Customer
                                        </Button>
                                        {this.state.activeSection === "FFR Concessions" && (
                                            <>
                                                <Button
                                                    variant={this.state.sortOrder === "oldest" ? "primary" : "normal"}
                                                    onClick={() => this.setState({ sortOrder: "oldest" })}
                                                >
                                                    Oldest
                                                </Button>
                                                <Button
                                                    variant={this.state.sortOrder === "newest" ? "primary" : "normal"}
                                                    onClick={() => this.setState({ sortOrder: "newest" })}
                                                >
                                                    Newest
                                                </Button>
                                            </>
                                        )}
                                        {this.renderCountBadge()}
                                    </SpaceBetween>
                                </SpaceBetween>
                            </Container>

                            {(this.state.isLoading || this.state.completingFills) && (
                                <Box textAlign="center" padding="xxl">
                                    <Spinner size="large" />
                                    {this.state.completingFills && (
                                        <Box padding={{ top: "s" }} color="text-body-secondary">
                                            Completing fills for Consumption Guard customer...
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {this.state.errorMessage && (
                                <Alert
                                    type="error"
                                    dismissible
                                    onDismiss={() => this.setState({ errorMessage: "" })}
                                >
                                    {this.state.errorMessage}
                                </Alert>
                            )}

                            {this.state.resultSuccessful && (
                                <Container header={<Header variant="h2">Customer Details</Header>}>
                                    <CreateSubscriptionResult
                                        resultSuccessful={this.state.resultSuccessful}
                                        response={this.state.response}
                                    />
                                </Container>
                            )}

                            {!this.state.completingFills && this.state.completeFillResults.length > 0 && (
                                <Container header={<Header variant="h2">Complete Fill Results</Header>}>
                                    <SpaceBetween size="xs">
                                        {this.state.completeFillResults.map((result, idx) => (
                                            <div key={idx}>
                                                <StatusIndicator type={result.success ? "success" : "error"}>
                                                    Fill {result.fillId} (Rx: {result.prescriptionId})
                                                    {result.success ? " — Completed" : ` — Failed: ${result.message}`}
                                                </StatusIndicator>
                                            </div>
                                        ))}
                                    </SpaceBetween>
                                </Container>
                            )}
                        </SpaceBetween>
                    </div>
                }
            />
        );
    }
}

export default CachedPharmacyCustomerProviderPage;
